import {
  type MediaCardType,
  type MediaString,
} from '@wandb/weave/common/types/media';
import {uuidv4} from '@wandb/weave/core/util/id';
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {useParams} from 'react-router-dom';

import {recordRumDurationVital} from '../../util/datadog/datadogRum';
import {
  useRampFlagMediaPanelReactFetch,
  useRampFlagMediaPanelTracking,
  useRampFlagMediaPanelTrackingDebug,
} from '../../util/rampFeatureFlags/rampFeatureFlags';
import {type MediaConfigWithDefaultsAndMediaKeys} from './Component';
import {
  type LoadTier,
  type TileCountBucket,
} from './modes/trackingConfigHelper';
import {type PanelBuildMetrics} from './utils/tracking';
import {getTrackingSegmentation} from './utils/tracking';

const supportedMediaTypes: MediaCardType[] = [
  'image',
  'video',
  'audio',
  'object3D',
  'plotly',
  'molecule',
  'html',
];
const DD_RUM_METRIC = 'mediaBrowserPanelTracking';
const DD_RUM_DESCRIPTION = 'Media browser panel load duration';

export type MediaBrowserPanelTrackingContextType = {
  state: MediaBrowserPanelTrackingContextState;
  /** Internal build-stage sink; not included in emitted vital context. */
  panelBuildMetricsRef?: React.RefObject<PanelBuildMetrics>;
  isMediaBrowserPanelTrackingEnabled: boolean;
  isMediaBrowserPanelTrackingDebugEnabled: boolean;
  isMediaBrowserPanelTrackingSupported: boolean;
  incrementCompleted: () => void;
};

export type MediaBrowserPanelTrackingContextState = {
  panelID: string;
  panelLoadUUID: string;
  mediaType?: MediaCardType | null;
  mediaString?: MediaString | null;
  /** Tiles that mount and fetch on the current page (completion denominator). */
  tileCount: number;
  /** Total tiles in the layout before pagination. */
  layoutTileCount: number;
  /** Concurrent mounts/fetches for the current panel view. */
  activeTileCount: number;
  panelMode: MediaConfigWithDefaultsAndMediaKeys['mode'];
  pageCap: number | null;
  loadTier: LoadTier;
  tileCountBucket: TileCountBucket;
  /**
   * `media_panel_react_fetch` gate at panel-tracking mount. On tile/panel
   * vitals so stage charts can split gate-on vs gate-off without joining
   * Feature Flag attributes.
   */
  reactFetchEnabled: boolean;

  // To prevent clock skew, we have to use performance.now() instead of Date.now()
  // to measure duration
  endTimePerformanceMs?: number;
  /** Provider-mount origin for legacy total vitals (tile-load window). */
  startTimePerformanceMs?: number;
  /** First MediaBrowserPanelComp render; origin for build-phase stage vitals. */
  wallClockStartPerformanceMs?: number;
  /** Wall-clock build phase before the tracking provider mounts. */
  buildPhaseDurationMs?: number;

  // But Datadog requires startTimeMs so we also need to store Date.now()
  startTimeMs: number;
};

// Default values (can be empty or from initial props)
export const defaultState: MediaBrowserPanelTrackingContextState = {
  panelID: '',
  panelLoadUUID: '',
  mediaType: null,
  mediaString: null,
  startTimeMs: 0,
  tileCount: 0,
  layoutTileCount: 0,
  activeTileCount: 0,
  panelMode: 'gallery',
  pageCap: null,
  loadTier: 'single',
  tileCountBucket: '1',
  reactFetchEnabled: false,
};

export const defaultContext: MediaBrowserPanelTrackingContextType = {
  state: defaultState,
  isMediaBrowserPanelTrackingEnabled: false,
  isMediaBrowserPanelTrackingDebugEnabled: false,
  isMediaBrowserPanelTrackingSupported: false,
  incrementCompleted: () => {},
};

export const MediaBrowserPanelTrackingContext =
  createContext<MediaBrowserPanelTrackingContextType>({
    state: defaultState,
    isMediaBrowserPanelTrackingEnabled: false,
    isMediaBrowserPanelTrackingDebugEnabled: false,
    isMediaBrowserPanelTrackingSupported: false,
    incrementCompleted: () => {},
  });

export const useMediaBrowserPanelTrackingContext = () => {
  const context = useContext(MediaBrowserPanelTrackingContext);
  if (!context) {
    throw new Error(
      'useMediaBrowserPanelTrackingContext must be used within a MediaBrowserPanelTrackingContextProvider'
    );
  }
  return context;
};

export const useMediaBrowserPanelTrackingContextMaybe = () => {
  const context = useContext(MediaBrowserPanelTrackingContext);
  if (!context) {
    return defaultContext;
  }
  return context;
};

export type MediaBrowserPanelTrackingContextProviderProps = {
  children: React.ReactNode;
  panelId?: string;
  mediaType?: MediaCardType | null;
  mediaString?: MediaString | null;
  config: MediaConfigWithDefaultsAndMediaKeys;
  /** Tiles that mount and fetch on the current page. */
  activeTileCount: number;
  /** Total tiles in the layout before pagination. */
  layoutTileCount: number;
  compareRowCount: number;
  /** Wall-clock origin for this panel load; should be set on first panel render. */
  panelLoadStartPerformanceMs: number;
  panelBuildMetricsRef?: React.RefObject<PanelBuildMetrics>;
};

type StateRef = {
  state: MediaBrowserPanelTrackingContextState;
  completed: number;
};

export const MediaBrowserPanelTrackingContextProvider = React.memo(
  ({
    mediaType,
    mediaString,
    panelId,
    config,
    activeTileCount,
    layoutTileCount,
    compareRowCount,
    panelLoadStartPerformanceMs,
    panelBuildMetricsRef,
    children,
  }: MediaBrowserPanelTrackingContextProviderProps) => {
    const {entityName} = useParams<{entityName: string}>();
    const isMediaPanelTrackingEnabled =
      useRampFlagMediaPanelTracking(entityName);
    const isDebugMediaPanelTrackingEnabled =
      useRampFlagMediaPanelTrackingDebug();
    const reactFetchEnabled = useRampFlagMediaPanelReactFetch() ?? false;
    const isMediaTypeSupported =
      mediaType && supportedMediaTypes.includes(mediaType);
    const [hasEmitted, setHasEmitted] = useState(false);
    const isPanelTrackingEnabled =
      !!isMediaPanelTrackingEnabled && !!isMediaTypeSupported && !hasEmitted;

    const segmentation = getTrackingSegmentation({
      activeTileCount,
      layoutTileCount,
      panelMode: config.mode,
      mediaType,
      columnCount: config.columnCount ?? 1,
      compareRowCount,
      fanOutVariable: !!config.compareSettings?.fanOutVariable,
    });

    // oxlint-disable-next-line react/purity
    const trackingStartPerformanceMs = performance.now();

    // Move all mutable state into a single ref
    const stateRef = useRef<StateRef>({
      state: {
        ...defaultState,
        panelLoadUUID: uuidv4(),
        // oxlint-disable-next-line react/purity
        startTimeMs: Date.now(),
        wallClockStartPerformanceMs: panelLoadStartPerformanceMs,
        startTimePerformanceMs: trackingStartPerformanceMs,
        buildPhaseDurationMs:
          trackingStartPerformanceMs - panelLoadStartPerformanceMs,
        panelID: panelId ?? '',
        mediaType,
        mediaString,
        tileCount: activeTileCount,
        reactFetchEnabled,
        ...segmentation,
      },
      completed: 0,
    });
    // Keep state.current in sync with prop changes for dynamic fields
    React.useEffect(() => {
      const nextSegmentation = getTrackingSegmentation({
        activeTileCount,
        layoutTileCount,
        panelMode: config.mode,
        mediaType,
        columnCount: config.columnCount ?? 1,
        compareRowCount,
        fanOutVariable: !!config.compareSettings?.fanOutVariable,
      });
      stateRef.current.state.mediaType = mediaType;
      stateRef.current.state.mediaString = mediaString;
      stateRef.current.state.tileCount = activeTileCount;
      stateRef.current.state.panelID = panelId ?? '';
      stateRef.current.state.reactFetchEnabled = reactFetchEnabled;
      Object.assign(stateRef.current.state, nextSegmentation);
    }, [
      mediaType,
      mediaString,
      activeTileCount,
      layoutTileCount,
      config,
      compareRowCount,
      panelId,
      reactFetchEnabled,
    ]);

    const loggingPrefix = useRef(
      `MediaBrowserPanelTracking ${panelId} panel ${mediaType}`
    );

    const log = useCallback(
      (event: string, msg: any) => {
        if (isDebugMediaPanelTrackingEnabled) {
          console.log(loggingPrefix.current, event, JSON.stringify(msg));
        }
      },
      [isDebugMediaPanelTrackingEnabled]
    );

    const getDuration = useCallback(() => {
      if (
        stateRef.current.state.startTimePerformanceMs == null ||
        stateRef.current.state.endTimePerformanceMs == null
      ) {
        return 0;
      }
      return (
        stateRef.current.state.endTimePerformanceMs -
        stateRef.current.state.startTimePerformanceMs
      );
    }, []);

    // Memoize the vital data calculation
    const getVital = useCallback(() => {
      return {
        startTime: stateRef.current.state.startTimeMs,
        description: DD_RUM_DESCRIPTION,
        duration: getDuration(),
        context: stateRef.current.state,
      };
    }, [getDuration]);

    // Memoize the emit function
    const emit = useCallback(() => {
      if (!isPanelTrackingEnabled) {
        return;
      }
      setHasEmitted(true);

      const ddContext = getVital();
      try {
        recordRumDurationVital(DD_RUM_METRIC, ddContext);
      } catch (error) {
        console.error('Error adding duration vital', error);
      } finally {
        if (isDebugMediaPanelTrackingEnabled) {
          log('emit', ddContext);
        }
      }
    }, [
      isPanelTrackingEnabled,
      isDebugMediaPanelTrackingEnabled,
      log,
      getVital,
    ]);

    // Memoize the increment function with stable dependencies
    const incrementCompleted = useCallback(() => {
      stateRef.current.completed++;
      if (
        stateRef.current.state.tileCount > 0 &&
        stateRef.current.completed === stateRef.current.state.tileCount
      ) {
        stateRef.current.state.endTimePerformanceMs = performance.now();
        emit();
      }
    }, [emit]);

    const contextValue = useMemo(() => {
      // Only expose tracking as enabled and the real incrementCompleted
      // if panel tracking is currently enabled (not emitted, supported type, etc)
      return {
        isMediaBrowserPanelTrackingEnabled: isPanelTrackingEnabled,
        isMediaBrowserPanelTrackingDebugEnabled:
          !!isDebugMediaPanelTrackingEnabled,
        isMediaBrowserPanelTrackingSupported: !!isMediaTypeSupported,
        // oxlint-disable-next-line react/refs
        state: stateRef.current.state,
        panelBuildMetricsRef,
        incrementCompleted: isPanelTrackingEnabled
          ? incrementCompleted
          : () => {},
      };
    }, [
      incrementCompleted,
      isPanelTrackingEnabled,
      isDebugMediaPanelTrackingEnabled,
      isMediaTypeSupported,
      panelBuildMetricsRef,
    ]);

    return (
      // oxlint-disable-next-line react/refs
      <MediaBrowserPanelTrackingContext.Provider value={contextValue}>
        {children}
      </MediaBrowserPanelTrackingContext.Provider>
    );
  }
);
